import { useState, useMemo, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { 
  ChevronDown, 
  ChevronRight, 
  Target,
  Layers,
  Goal,
  AlertTriangle,
  Sparkles,
  Users,
  BarChart3
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { performanceService, type RoleKPI } from '@/services/api/performanceService'

interface KPI {
  id: string
  name: string
  description: string
  target_value: number
  current_value: number
  unit: string
  status: 'on-track' | 'at-risk' | 'off-track'
  frequency: string
  owner: string
  functional_area: string
  progress_percentage?: number
}

interface IndividualObjective {
  id: number
  title: string
  description: string | null
  role_type: string
  owner: string
  functional_area: string
  status: string
  progress: number
  start_date: string | null
  end_date: string | null
}

interface StrategicInitiative {
  id: number | string
  name: string
  description: string | null
  initiative_type: string
  status: string
  owner: string | null
  start_date: string | null
  end_date: string | null
}

interface CascadeData {
  pillar_name: string
  strategic_objective_name: string
  kpa_name: string
  individual_objective?: IndividualObjective
  primary_initiative?: StrategicInitiative
  kpis: KPI[]
  strategic_context?: {
    l1?: {
      id: number
      name: string
      display_name: string
      code: string
    } | null
    l2?: {
      id: number
      code: string
      name: string
      description: string | null
    } | null
    l3?: {
      id: number
      code: string
      name: string
      description: string | null
    } | null
  }
}

interface StrategicCascadeViewerProps {
  cascadeData?: CascadeData[] // Now optional, will fetch from API if not provided
  roleType?: string // Role type to fetch data for
  onSelectKPI?: (kpi: KPI) => void
  onSelectObjective?: (objective: IndividualObjective) => void
  compact?: boolean
}

type IndividualObjectiveItem = {
  id: number
  title: string
  description: string | null
  progress: number
  status: string
  owner: string
  kpis: KPI[]
  kpa_name: string  // KPA shown as tag under objective
  initiative?: StrategicInitiative
  strategic_context?: {
    l1?: { id: number; name: string; display_name: string; code: string } | null
    l2?: { id: number; code: string; name: string; description: string | null } | null
    l3?: { id: number; code: string; name: string; description: string | null } | null
  }
}

type PillarStructure = {
  name: string  // L1: Strategic Pillar (SCALE, QUALITY, CULTURE, IMPACT)
  objectives: {  // L2: Strategic Initiative
    name: string
    code?: string
    operationalInitiatives: {  // L3: Operational Initiative
      name: string
      code?: string
      individualObjectives: IndividualObjectiveItem[]  // Individual objectives directly under L3
    }[]
  }[]
}

export default function StrategicCascadeViewer({
  cascadeData: propCascadeData,
  roleType: propRoleType = 'lecturer',
  onSelectKPI,
  onSelectObjective: _onSelectObjective,
  compact = false
}: StrategicCascadeViewerProps) {
  // Normalize role type to lowercase and remove spaces/hyphens
  const roleType = propRoleType.toLowerCase().replace(/\s+/g, '-')
  const [expandedPillars, setExpandedPillars] = useState<Set<string>>(new Set())
  const [expandedObjectives, setExpandedObjectives] = useState<Set<string>>(new Set())
  const [expandedL3s, setExpandedL3s] = useState<Set<string>>(new Set())  // L3: Operational Initiatives
  const [expandedIndividualObjectives, setExpandedIndividualObjectives] = useState<Set<number>>(new Set())
  
  // State for API data
  const [roleKPIs, setRoleKPIs] = useState<RoleKPI[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch real KPI data from API only if cascadeData is not provided via props
  useEffect(() => {
    // Skip API call if cascadeData is already provided
    if (propCascadeData && propCascadeData.length > 0) {
      return
    }

    const loadRoleKPIs = async () => {
      if (!roleType) return

      setIsLoading(true)
      setError(null)
      try {
        const kpis = await performanceService.getRoleKPIs(roleType)
        setRoleKPIs(kpis)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load KPIs')
        console.error('Failed to load role KPIs for cascade:', err)
        setRoleKPIs([])
      } finally {
        setIsLoading(false)
      }
    }

    loadRoleKPIs()
  }, [roleType, propCascadeData])

  // Transform API data into cascade format
  const apiCascadeData = useMemo<CascadeData[]>(() => {
    if (roleKPIs.length === 0) return []

    return roleKPIs.map((kpi) => ({
      pillar_name: kpi.pillar_name || 'Uncategorized',
      strategic_objective_name: kpi.strategic_objective_name || 'Unknown Objective',
      kpa_name: kpi.kpa_name || 'Unknown KPA',
      individual_objective: kpi.individual_objective ? {
        id: kpi.individual_objective.id,
        title: kpi.individual_objective.title,
        description: kpi.individual_objective.description || null,
        role_type: kpi.individual_objective.role_type,
        owner: kpi.individual_objective.owner,
        functional_area: kpi.individual_objective.functional_area,
        status: kpi.individual_objective.status,
        progress: kpi.individual_objective.progress,
        start_date: kpi.individual_objective.start_date || null,
        end_date: kpi.individual_objective.end_date || null
      } : undefined,
      primary_initiative: kpi.primary_initiative ? {
        id: kpi.primary_initiative.id,
        name: kpi.primary_initiative.name,
        description: kpi.primary_initiative.description || null,
        initiative_type: kpi.primary_initiative.initiative_type,
        status: kpi.primary_initiative.status,
        owner: kpi.primary_initiative.owner || null,
        start_date: kpi.primary_initiative.start_date || null,
        end_date: kpi.primary_initiative.end_date || null
      } : undefined,
      kpis: [{
        id: kpi.id,
        name: kpi.name,
        description: kpi.description,
        target_value: kpi.target_value,
        current_value: kpi.current_value || 0,
        unit: kpi.unit,
        status: kpi.status,
        frequency: kpi.frequency,
        owner: kpi.owner || 'Unassigned',
        functional_area: kpi.functional_area,
        progress_percentage: kpi.progress_percentage || undefined
      }]
    }))
  }, [roleKPIs])

  // Use API data if available, otherwise fall back to prop data
  const cascadeData = apiCascadeData.length > 0 ? apiCascadeData : (propCascadeData || [])

  // Transform flat cascade data into hierarchical structure with L1→L2→L3→Objectives (KPA removed as level, shown as tag)
  const hierarchicalData = useMemo<PillarStructure[]>(() => {
    const pillarsMap = new Map<string, PillarStructure>()

    cascadeData.forEach((item) => {
      // L1: Get or create pillar (Strategic Objective: SCALE, QUALITY, CULTURE, IMPACT)
      if (!pillarsMap.has(item.pillar_name)) {
        pillarsMap.set(item.pillar_name, {
          name: item.pillar_name,
          objectives: []
        })
      }
      const pillar = pillarsMap.get(item.pillar_name)!

      // L2: Get or create strategic initiative (from strategic_context.l2 or strategic_objective_name)
      const l2Name = item.strategic_context?.l2?.name || item.strategic_objective_name
      const l2Code = item.strategic_context?.l2?.code
      let objective = pillar.objectives.find(o => o.name === l2Name)
      if (!objective) {
        objective = {
          name: l2Name,
          code: l2Code,
          operationalInitiatives: []
        }
        pillar.objectives.push(objective)
      }

      // L3: Get or create operational initiative (from strategic_context.l3)
      const l3Name = item.strategic_context?.l3?.name || 'General'
      const l3Code = item.strategic_context?.l3?.code
      let opInit = objective.operationalInitiatives.find(oi => oi.name === l3Name)
      if (!opInit) {
        opInit = {
          name: l3Name,
          code: l3Code,
          individualObjectives: []
        }
        objective.operationalInitiatives.push(opInit)
      }

      // Add individual objective directly under L3 (no KPA level)
      if (item.individual_objective) {
        const existingIndivObj = opInit.individualObjectives.find(
          io => io.id === item.individual_objective!.id
        )

        if (!existingIndivObj) {
          opInit.individualObjectives.push({
            id: item.individual_objective.id,
            title: item.individual_objective.title,
            description: item.individual_objective.description,
            progress: item.individual_objective.progress,
            status: item.individual_objective.status,
            owner: item.individual_objective.owner,
            kpis: item.kpis,
            kpa_name: item.kpa_name,  // Store KPA name for display as tag
            initiative: item.primary_initiative,
            strategic_context: item.strategic_context
          })
        } else {
          // Add KPIs to existing individual objective
          existingIndivObj.kpis.push(...item.kpis)
        }
      }
    })

    // Sort pillars in strategic order: Impact, Culture, Quality, Scale
    const pillarOrder = ['impact', 'culture', 'quality', 'scale']
    const sortedPillars = Array.from(pillarsMap.values()).sort((a, b) => {
      const aIndex = pillarOrder.indexOf(a.name.toLowerCase())
      const bIndex = pillarOrder.indexOf(b.name.toLowerCase())
      // If not in the order list, put at the end
      const aOrder = aIndex === -1 ? 999 : aIndex
      const bOrder = bIndex === -1 ? 999 : bIndex
      return aOrder - bOrder
    })

    return sortedPillars
  }, [cascadeData])

  // Calculate statistics
  const stats = useMemo(() => {
    const allKPIs = cascadeData.flatMap(d => d.kpis)
    const uniqueObjectives = new Set(cascadeData.map(d => d.individual_objective?.id).filter(Boolean))
    
    // Count unique KPAs from individual objectives
    const uniqueKPAs = new Set(cascadeData.map(d => d.kpa_name).filter(Boolean))
    
    return {
      totalPillars: hierarchicalData.length,
      totalStrategicObjectives: hierarchicalData.reduce((sum, p) => sum + p.objectives.length, 0),
      totalKPAs: uniqueKPAs.size,
      totalIndividualObjectives: uniqueObjectives.size,
      totalKPIs: allKPIs.length,
      onTrackKPIs: allKPIs.filter(k => k.status === 'on-track').length,
      atRiskKPIs: allKPIs.filter(k => k.status === 'at-risk').length,
      offTrackKPIs: allKPIs.filter(k => k.status === 'off-track').length
    }
  }, [cascadeData, hierarchicalData])

  const togglePillar = (pillarName: string) => {
    setExpandedPillars(prev => {
      const next = new Set(prev)
      if (next.has(pillarName)) {
        next.delete(pillarName)
      } else {
        next.add(pillarName)
      }
      return next
    })
  }

  const toggleObjective = (objectiveName: string) => {
    setExpandedObjectives(prev => {
      const next = new Set(prev)
      if (next.has(objectiveName)) {
        next.delete(objectiveName)
      } else {
        next.add(objectiveName)
      }
      return next
    })
  }

  const toggleIndividualObjective = (objectiveId: number) => {
    setExpandedIndividualObjectives(prev => {
      const next = new Set(prev)
      if (next.has(objectiveId)) {
        next.delete(objectiveId)
      } else {
        next.add(objectiveId)
      }
      return next
    })
  }

  const toggleL3 = (l3Name: string) => {
    setExpandedL3s(prev => {
      const next = new Set(prev)
      if (next.has(l3Name)) {
        next.delete(l3Name)
      } else {
        next.add(l3Name)
      }
      return next
    })
  }

  const expandAll = () => {
    setExpandedPillars(new Set(hierarchicalData.map(p => p.name)))
    setExpandedObjectives(new Set(hierarchicalData.flatMap(p => p.objectives.map(o => o.name))))
    setExpandedL3s(new Set(hierarchicalData.flatMap(p => 
      p.objectives.flatMap(o => o.operationalInitiatives.map(oi => oi.name))
    )))
    setExpandedIndividualObjectives(new Set(hierarchicalData.flatMap(p =>
      p.objectives.flatMap(o => o.operationalInitiatives.flatMap(oi => oi.individualObjectives.map(io => io.id)))
    )))
  }

  const collapseAll = () => {
    setExpandedPillars(new Set())
    setExpandedObjectives(new Set())
    setExpandedL3s(new Set())
    setExpandedIndividualObjectives(new Set())
  }

  const getPillarColor = (pillarName: string): { bg: string; text: string; border: string } => {
    // Colors aligned with 2026 Strategy on a page diagram (exact hex values)
    // Text colors optimized for WCAG AA contrast ratio
    switch (pillarName.toLowerCase()) {
      case 'impact':
        // Dark blue - #1B4087
        return { bg: '#1B4087', text: 'white', border: '#152f63' }
      case 'culture':
        // Tan/beige - #CFAD80 - dark brown text for contrast
        return { bg: '#CFAD80', text: '#3d2e1a', border: '#b8976a' }
      case 'scale':
        // Green - #85BF3F - dark green text for contrast
        return { bg: '#85BF3F', text: '#1a3d0a', border: '#6a9932' }
      case 'quality':
        // Light blue - #97B7FC - dark blue text for contrast
        return { bg: '#97B7FC', text: '#0f2952', border: '#7a9fea' }
      default:
        return { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' }
    }
  }

  // Show loading state
  if (isLoading && cascadeData.length === 0) {
    return (
      <Card className="bg-gray-50">
        <CardContent className="pt-6 pb-6">
          <div className="text-center text-gray-500">
            <Layers className="h-12 w-12 mx-auto mb-3 opacity-50 animate-pulse" />
            <p className="font-medium">Loading strategic cascade...</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Show error state
  if (error && cascadeData.length === 0) {
    return (
      <Card className="bg-red-50 border-red-200">
        <CardContent className="pt-6 pb-6">
          <div className="text-center text-red-700">
            <AlertTriangle className="h-12 w-12 mx-auto mb-3" />
            <p className="font-medium">Failed to load cascade data</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {!compact && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <Sparkles className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-purple-700">{stats.totalPillars}</p>
                <p className="text-xs text-purple-600 font-medium">Strategic Objectives</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <Target className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-blue-700">{stats.totalStrategicObjectives}</p>
                <p className="text-xs text-blue-600 font-medium">Strategic Initiatives</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <Layers className="h-5 w-5 text-green-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-green-700">{stats.totalKPAs}</p>
                <p className="text-xs text-green-600 font-medium">KPAs</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <Goal className="h-5 w-5 text-orange-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-orange-700">{stats.totalIndividualObjectives}</p>
                <p className="text-xs text-orange-600 font-medium">Individual Objectives</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-heritage-blue/10 to-white border-heritage-blue/30">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <BarChart3 className="h-5 w-5 text-heritage-blue mx-auto mb-1" />
                <p className="text-2xl font-bold text-heritage-blue">{stats.totalKPIs}</p>
                <p className="text-xs text-heritage-blue font-medium">KPIs</p>
              </div>
            </CardContent>
          </Card>

        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-heritage-blue" />
          <h3 className="text-lg font-semibold">Strategic Cascade Structure</h3>
          {roleType && (
            <Badge variant="outline" className="ml-2">
              {roleType}
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={expandAll}>
            Expand All
          </Button>
          <Button size="sm" variant="outline" onClick={collapseAll}>
            Collapse All
          </Button>
        </div>
      </div>

      {/* Hierarchical Cascade Visualization */}
      <div className="space-y-3">
        {hierarchicalData.map((pillar) => (
          <motion.div
            key={pillar.name}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="border rounded-lg overflow-hidden"
          >
            {/* L1: Strategic Objective Level */}
            {(() => {
              const colors = getPillarColor(pillar.name)
              return (
                <div
                  className="p-4 cursor-pointer hover:brightness-95 transition-all border-l-4"
                  style={{ 
                    backgroundColor: colors.bg, 
                    color: colors.text, 
                    borderLeftColor: colors.border 
                  }}
                  onClick={() => togglePillar(pillar.name)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {expandedPillars.has(pillar.name) ? (
                        <ChevronDown className="h-5 w-5 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-5 w-5 flex-shrink-0" />
                      )}
                      <Sparkles className="h-5 w-5 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-lg">Strategic Objective: {pillar.name}</p>
                        <p className="text-sm opacity-80">
                          {pillar.objectives.length} Strategic Initiative{pillar.objectives.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-white/50 text-inherit border-white/30">
                      Level 1
                    </Badge>
                  </div>
                </div>
              )
            })()}

            <AnimatePresence>
              {expandedPillars.has(pillar.name) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-gray-50"
                >
                  <div className="p-4 pl-12 space-y-3">
                    {pillar.objectives.map((objective) => (
                      <div key={objective.name} className="bg-white rounded-lg border shadow-sm">
                        {/* L2: Strategic Initiative Level */}
                        <div
                          className="p-3 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 border-blue-400"
                          onClick={() => toggleObjective(objective.name)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {expandedObjectives.has(objective.name) ? (
                                <ChevronDown className="h-4 w-4 flex-shrink-0 text-blue-600" />
                              ) : (
                                <ChevronRight className="h-4 w-4 flex-shrink-0 text-blue-600" />
                              )}
                              <Target className="h-4 w-4 flex-shrink-0 text-blue-600" />
                              <div>
                                <p className="font-semibold text-blue-900">
                                  {objective.code && <span className="text-blue-500 mr-1">{objective.code}</span>}
                                  {objective.name}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {objective.operationalInitiatives.length} Operational Objective{objective.operationalInitiatives.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                              Level 2
                            </Badge>
                          </div>
                        </div>

                        <AnimatePresence>
                          {expandedObjectives.has(objective.name) && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="bg-gray-50"
                            >
                              <div className="p-3 pl-10 space-y-2">
                                {objective.operationalInitiatives.map((opInit) => (
                                  <div key={opInit.name} className="bg-white rounded-lg border">
                                    {/* L3: Operational Objective Level */}
                                    <div
                                      className="p-3 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 border-green-400"
                                      onClick={() => toggleL3(opInit.name)}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          {expandedL3s.has(opInit.name) ? (
                                            <ChevronDown className="h-4 w-4 flex-shrink-0 text-green-600" />
                                          ) : (
                                            <ChevronRight className="h-4 w-4 flex-shrink-0 text-green-600" />
                                          )}
                                          <Layers className="h-4 w-4 flex-shrink-0 text-green-600" />
                                          <div>
                                            <p className="font-semibold text-sm text-green-900">
                                              {opInit.code && <span className="text-green-500 mr-1">{opInit.code}</span>}
                                              {opInit.name}
                                            </p>
                                            <p className="text-xs text-gray-600">
                                              {opInit.individualObjectives.length} Individual Objective{opInit.individualObjectives.length !== 1 ? 's' : ''}
                                            </p>
                                          </div>
                                        </div>
                                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                                          Level 3
                                        </Badge>
                                      </div>
                                    </div>

                                    <AnimatePresence>
                                      {expandedL3s.has(opInit.name) && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: 'auto', opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          transition={{ duration: 0.2 }}
                                          className="bg-gray-50"
                                        >
                                          <div className="p-3 pl-8 space-y-2">
                                            {/* Individual Objectives directly under L3 (no KPA grouping) */}
                                            {opInit.individualObjectives.map((indivObj) => (
                                              <div key={indivObj.id} className="bg-white rounded-lg border">
                                                {/* Individual Objective Level (Level 4) */}
                                                <div
                                                  className="p-3 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 border-orange-400"
                                                  onClick={() => toggleIndividualObjective(indivObj.id)}
                                                >
                                                  <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2 flex-1">
                                                      {expandedIndividualObjectives.has(indivObj.id) ? (
                                                        <ChevronDown className="h-4 w-4 flex-shrink-0 text-orange-600" />
                                                      ) : (
                                                        <ChevronRight className="h-4 w-4 flex-shrink-0 text-orange-600" />
                                                      )}
                                                      <Goal className="h-4 w-4 flex-shrink-0 text-orange-600" />
                                                      <div className="flex-1 min-w-0">
                                                        <p className="font-semibold text-sm text-orange-900 truncate">
                                                          {indivObj.title}
                                                        </p>
                                                        <p className="text-xs text-gray-600 line-clamp-1">
                                                          {indivObj.description || 'No description available'}
                                                        </p>
                                                      </div>
                                                    </div>
                                                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-xs">
                                                      Level 4
                                                    </Badge>
                                                  </div>

                                                  <div className="flex items-center gap-3 text-xs flex-wrap">
                                                    {/* KPA shown as tag */}
                                                    <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-300 text-xs">
                                                      <Layers className="h-3 w-3 mr-1" />
                                                      {indivObj.kpa_name}
                                                    </Badge>
                                                    <div className="flex items-center gap-1">
                                                      <Users className="h-3 w-3 text-gray-500" />
                                                      <span className="text-gray-600">{indivObj.owner}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <Progress value={indivObj.progress} className="w-20 h-2" />
                                                      <span className="text-gray-600 font-medium">{indivObj.progress}%</span>
                                                    </div>
                                                  </div>
                                                </div>

                                                {/* Level 5: KPIs */}
                                                <AnimatePresence>
                                                  {expandedIndividualObjectives.has(indivObj.id) && (
                                                    <motion.div
                                                      initial={{ height: 0, opacity: 0 }}
                                                      animate={{ height: 'auto', opacity: 1 }}
                                                      exit={{ height: 0, opacity: 0 }}
                                                      transition={{ duration: 0.2 }}
                                                      className="bg-gradient-to-br from-gray-50 to-white"
                                                    >
                                                      <div className="p-3 pl-6 space-y-2">
                                                        <div className="flex items-center gap-2 mb-2">
                                                          <BarChart3 className="h-4 w-4 text-heritage-blue" />
                                                          <p className="text-xs font-semibold text-heritage-blue">
                                                            KPIs ({indivObj.kpis.length})
                                                          </p>
                                                        </div>
                                                        {indivObj.kpis.map((kpi) => (
                                                          <div
                                                            key={kpi.id}
                                                            className="p-3 bg-white border rounded-lg hover:shadow-md transition-all cursor-pointer border-l-4 border-heritage-blue"
                                                            onClick={() => onSelectKPI?.(kpi)}
                                                          >
                                                            <div className="flex items-start justify-between gap-3">
                                                              <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                  <p className="font-semibold text-sm text-gray-900">{kpi.name}</p>
                                                                </div>
                                                                <div className="flex items-center gap-4 text-xs text-gray-600">
                                                                  <span>Current: <span className="font-semibold">{kpi.current_value}{kpi.unit}</span></span>
                                                                  <span>Target: <span className="font-semibold">{kpi.target_value}{kpi.unit}</span></span>
                                                                </div>
                                                              </div>
                                                              <Badge variant="outline" className="bg-heritage-blue/10 text-heritage-blue border-heritage-blue/30 text-xs">
                                                                Level 5
                                                              </Badge>
                                                            </div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </motion.div>
                                                  )}
                                                </AnimatePresence>
                                              </div>
                                            ))}
                                          </div>
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {hierarchicalData.length === 0 && (
        <Card className="bg-gray-50 border-dashed">
          <CardContent className="pt-6 pb-6">
            <div className="text-center text-gray-500">
              <Layers className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No cascade data available</p>
              <p className="text-sm mt-1">Configure objectives to see strategic alignment</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
